/**
 * Dimension normalization + formatting utilities.
 *
 * Why this exists
 * ===============
 * Product dimension fields (`width_in`, `height_in`, `depth_in`) are nominally
 * named "in" / "inches", but real DB data has at least three corruption flavors:
 *
 *   • Samsung-style mm-stored-as-inches:
 *     RF23DB9750QLAA  width_in=912, height_in=1853, depth_in=731
 *     → these are clearly mm (912 mm = 35.9", 1853 mm = 73", 731 mm = 28.8")
 *
 *   • Frigidaire-style "inches×10" decimal corruption:
 *     FRSC2333AS  width_in=361.8, height_in=685.8, depth_in=45
 *     → first two look like inches with a missing decimal point;
 *       depth=45 is also wrong but in a different way. Genuinely ambiguous.
 *
 *   • Mixed/partial: only some axes populated, with conflicting units.
 *
 * Renderers (spec table, FAQ generator, SEO LLM content, JSON-LD) used to
 * read these fields raw and emit `912" × 1853" × 731"`. Bug 2026-05-07.
 *
 * The contract
 * ============
 *   • `normalizeDimensions(raw, type?)` — converts whatever's in the DB into
 *     normalized inches, returning a status describing what happened.
 *   • `formatDimensions(normalized)` — emits canonical display strings.
 *   • `sanityCheckDimensions(...)` — flags impossible values for quarantine.
 *
 * Renderers should call `normalizeDimensions` once at the boundary and pass
 * the result through `formatDimensions`. Never read `width_in` directly into
 * a string template again.
 */

/** Conversion constants. Single source of truth — don't inline elsewhere. */
const MM_PER_INCH = 25.4
const CM_PER_INCH = 2.54

/**
 * Status describing how the input was interpreted.
 *
 *   `explicit_inches` — values look plausibly like inches, used as-is.
 *   `inferred_mm`     — values clearly mm-range; converted by /25.4.
 *   `inferred_cm`     — values plausibly cm-range; converted by /2.54.
 *   `mixed`           — some axes interpretable, others not; partial output.
 *   `invalid`         — values are corrupted in a way we can't safely fix.
 *   `missing`         — no dimensions present at all.
 *
 * `invalid` and `missing` always render an empty string from
 * {@link formatDimensions} so we never put garbage in front of users.
 */
export type DimensionStatus =
  | 'explicit_inches'
  | 'inferred_mm'
  | 'inferred_cm'
  | 'mixed'
  | 'invalid'
  | 'missing'

export interface NormalizedDimensions {
  widthInches: number | null
  heightInches: number | null
  depthInches: number | null
  status: DimensionStatus
  /** When `status='mixed'` or `'invalid'`, explains which axis was rejected and why. */
  reason?: string
  /** The original raw values, kept for debugging/audit overlays. */
  raw?: {
    width: number | null | undefined
    height: number | null | undefined
    depth: number | null | undefined
  }
}

export interface RawDimensions {
  width_in?: number | null
  height_in?: number | null
  depth_in?: number | null
}

/**
 * Plausibility thresholds **in inches**, by product category.
 * If a value falls outside [minInches, maxInches] for its axis, it is rejected
 * as "not plausibly inches" and we attempt unit inference.
 *
 * Numbers are intentionally generous on the upper bound — a 36" wide,
 * 72" tall, 30" deep fridge is dead-center; a 50" wide commercial unit is
 * the high-side legitimate boundary.
 */
interface CategoryRange {
  width: { minInches: number; maxInches: number }
  height: { minInches: number; maxInches: number }
  depth: { minInches: number; maxInches: number }
}

const CATEGORY_RANGES: Record<string, CategoryRange> = {
  // Full-size refrigerator categories
  'french-door':       { width: { minInches: 22, maxInches: 50 }, height: { minInches: 60, maxInches: 90 }, depth: { minInches: 18, maxInches: 40 } },
  'side-by-side':      { width: { minInches: 22, maxInches: 50 }, height: { minInches: 60, maxInches: 90 }, depth: { minInches: 18, maxInches: 40 } },
  'top-freezer':       { width: { minInches: 18, maxInches: 50 }, height: { minInches: 50, maxInches: 90 }, depth: { minInches: 18, maxInches: 40 } },
  'bottom-freezer':    { width: { minInches: 18, maxInches: 50 }, height: { minInches: 50, maxInches: 90 }, depth: { minInches: 18, maxInches: 40 } },
  'counter-depth':     { width: { minInches: 22, maxInches: 50 }, height: { minInches: 60, maxInches: 90 }, depth: { minInches: 18, maxInches: 32 } },
  'refrigerator':      { width: { minInches: 18, maxInches: 50 }, height: { minInches: 50, maxInches: 90 }, depth: { minInches: 18, maxInches: 40 } },
  // Compact is not an undercounter-height guarantee. LG LRONC0605V is 44.625 in
  // tall; DOE removed the 36-in compact height condition. Reuse the general
  // refrigerator height ceiling as a plausibility guard, not a fit/certification
  // rule. Exact source and local regression evidence: FRIDGE-1307 in
  // apps/chatgpt-mcp/MANUFACTURER-DIMENSION-EVIDENCE.md.
  'compact':           { width: { minInches: 14, maxInches: 28 }, height: { minInches: 18, maxInches: 90 }, depth: { minInches: 14, maxInches: 28 } },
  'mini':              { width: { minInches: 14, maxInches: 28 }, height: { minInches: 14, maxInches: 36 }, depth: { minInches: 14, maxInches: 28 } },
  // Beverage / wine / specialty
  'wine-cooler':       { width: { minInches: 12, maxInches: 36 }, height: { minInches: 16, maxInches: 80 }, depth: { minInches: 16, maxInches: 30 } },
  'beverage-cooler':   { width: { minInches: 12, maxInches: 36 }, height: { minInches: 16, maxInches: 80 }, depth: { minInches: 16, maxInches: 30 } },
  'kegerator':         { width: { minInches: 16, maxInches: 36 }, height: { minInches: 30, maxInches: 80 }, depth: { minInches: 18, maxInches: 32 } },
  // Freezers
  'upright-freezer':   { width: { minInches: 18, maxInches: 36 }, height: { minInches: 50, maxInches: 80 }, depth: { minInches: 18, maxInches: 32 } },
  'chest-freezer':     { width: { minInches: 20, maxInches: 80 }, height: { minInches: 28, maxInches: 50 }, depth: { minInches: 20, maxInches: 36 } },
  'compact-freezer':   { width: { minInches: 14, maxInches: 24 }, height: { minInches: 18, maxInches: 36 }, depth: { minInches: 14, maxInches: 24 } },
  'freezer':           { width: { minInches: 14, maxInches: 80 }, height: { minInches: 18, maxInches: 80 }, depth: { minInches: 14, maxInches: 36 } },
  // Misc
  'ice-maker':         { width: { minInches: 10, maxInches: 30 }, height: { minInches: 14, maxInches: 60 }, depth: { minInches: 12, maxInches: 30 } },
}

/** Generic fallback when product type doesn't match a known category. */
const FALLBACK_RANGE: CategoryRange = {
  width:  { minInches: 8, maxInches: 90 },
  height: { minInches: 8, maxInches: 100 },
  depth:  { minInches: 6, maxInches: 50 },
}

function getRange(productType?: string | null): CategoryRange {
  if (!productType) return FALLBACK_RANGE
  const t = productType.toLowerCase().trim().replace(/[\s_]+/g, '-')
  if (CATEGORY_RANGES[t]) return CATEGORY_RANGES[t]
  // Specific category tokens precede generic refrigerator/freezer. Otherwise
  // "compact-refrigerator" inherits the full-size minimum height, while
  // "compact-freezer-appliance" can inherit the broader compact width range.
  const matches = Object.keys(CATEGORY_RANGES).filter(key =>
    (`-${t}-`).includes(`-${key}-`)
  )
  const specific = matches.filter(key => key !== 'refrigerator' && key !== 'freezer')
    .sort((a, b) => b.length - a.length)
  return CATEGORY_RANGES[specific[0] ?? matches[0]] ?? FALLBACK_RANGE
}

/**
 * For a single axis: given a raw value, decide whether it's plausibly
 * inches, mm, cm, or unfixable. Conversion factor returned tells the caller
 * what to multiply by.
 */
type AxisInterpretation =
  | { kind: 'inches'; inches: number }
  | { kind: 'mm'; inches: number }
  | { kind: 'cm'; inches: number }
  | { kind: 'invalid'; reason: string }

function interpretAxis(
  value: number | null | undefined,
  axis: 'width' | 'height' | 'depth',
  range: CategoryRange,
): AxisInterpretation | null {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return null
  }

  const r = range[axis]

  // Within the inch range → inches.
  if (value >= r.minInches && value <= r.maxInches) {
    return { kind: 'inches', inches: value }
  }

  // Try mm interpretation. mm range = inch range × 25.4.
  const mmAsInches = value / MM_PER_INCH
  if (mmAsInches >= r.minInches && mmAsInches <= r.maxInches) {
    return { kind: 'mm', inches: mmAsInches }
  }

  // Try cm interpretation.
  const cmAsInches = value / CM_PER_INCH
  if (cmAsInches >= r.minInches && cmAsInches <= r.maxInches) {
    return { kind: 'cm', inches: cmAsInches }
  }

  return {
    kind: 'invalid',
    reason: `${axis}=${value} doesn't fit any plausible unit (range inches=${r.minInches}-${r.maxInches}, mm=${(r.minInches * MM_PER_INCH).toFixed(0)}-${(r.maxInches * MM_PER_INCH).toFixed(0)})`,
  }
}

/**
 * Normalize a raw `{ width_in, height_in, depth_in }` triple to inches.
 *
 * Consensus rule
 * --------------
 * If ALL three present axes agree on the same unit kind (all inches OR all mm
 * OR all cm), apply that interpretation uniformly — `status='explicit_inches'`,
 * `'inferred_mm'`, or `'inferred_cm'`.
 *
 * If axes disagree (one says mm, another says inches, etc.) the data is
 * corrupted — return `status='invalid'` with all three normalized values null.
 * Renderers will then omit the dimensions section entirely rather than emit a
 * confidently-wrong claim.
 *
 * If only one or two axes are present, use whatever consensus is available.
 *
 * @example
 *   normalizeDimensions({ width_in: 912, height_in: 1853, depth_in: 731 }, 'counter-depth')
 *   // → { widthInches: 35.9, heightInches: 73.0, depthInches: 28.8, status: 'inferred_mm' }
 *
 * @example
 *   normalizeDimensions({ width_in: 35.9, height_in: 72.9, depth_in: 28.8 }, 'french-door')
 *   // → { widthInches: 35.9, heightInches: 72.9, depthInches: 28.8, status: 'explicit_inches' }
 *
 * @example
 *   normalizeDimensions({ width_in: 361.8, height_in: 685.8, depth_in: 45 }, 'side-by-side')
 *   // → { widthInches: null, heightInches: null, depthInches: null, status: 'invalid', reason: ... }
 */
export function normalizeDimensions(
  raw: RawDimensions,
  productType?: string | null,
): NormalizedDimensions {
  const range = getRange(productType)

  const axes = {
    width: interpretAxis(raw.width_in, 'width', range),
    height: interpretAxis(raw.height_in, 'height', range),
    depth: interpretAxis(raw.depth_in, 'depth', range),
  }

  const present = Object.entries(axes).filter(([, v]) => v !== null) as Array<
    [keyof typeof axes, AxisInterpretation]
  >

  const rawSnapshot = {
    width: raw.width_in,
    height: raw.height_in,
    depth: raw.depth_in,
  }

  if (present.length === 0) {
    return {
      widthInches: null,
      heightInches: null,
      depthInches: null,
      status: 'missing',
      raw: rawSnapshot,
    }
  }

  // If any axis is invalid, the whole record is suspect.
  const invalid = present.find(([, v]) => v.kind === 'invalid')
  if (invalid) {
    return {
      widthInches: null,
      heightInches: null,
      depthInches: null,
      status: 'invalid',
      reason: (invalid[1] as Extract<AxisInterpretation, { kind: 'invalid' }>).reason,
      raw: rawSnapshot,
    }
  }

  // Need consensus on interpretation kind across present axes.
  const kinds = new Set(present.map(([, v]) => v.kind))
  if (kinds.size > 1) {
    return {
      widthInches: null,
      heightInches: null,
      depthInches: null,
      status: 'invalid',
      reason: `axes disagree on unit: ${present.map(([k, v]) => `${k}=${v.kind}`).join(', ')}`,
      raw: rawSnapshot,
    }
  }

  const consensusKind = [...kinds][0] as Exclude<AxisInterpretation['kind'], 'invalid'>

  const status: DimensionStatus =
    consensusKind === 'inches' ? 'explicit_inches'
    : consensusKind === 'mm' ? 'inferred_mm'
    : 'inferred_cm'

  // Round to 1 decimal — typical retail catalog precision.
  const round1 = (n: number) => Math.round(n * 10) / 10

  const get = (axis: 'width' | 'height' | 'depth'): number | null => {
    const v = axes[axis]
    if (!v || v.kind === 'invalid') return null
    return round1(v.inches)
  }

  return {
    widthInches: get('width'),
    heightInches: get('height'),
    depthInches: get('depth'),
    status,
    raw: rawSnapshot,
  }
}

/**
 * Format a single inch value: `35.9` → `"35.9\""`. Drops the decimal when
 * the value is a clean integer (e.g. `36` → `"36\""` not `"36.0\""`).
 */
export function formatDimensionInches(inches: number | null | undefined): string {
  if (inches === null || inches === undefined || !Number.isFinite(inches) || inches <= 0) return ''
  if (Number.isInteger(inches)) return `${inches}"`
  return `${inches.toFixed(1)}"`
}

export interface FormatDimensionsOptions {
  /**
   * `'compact'` → `35.9" × 72.9" × 28.8"` (legacy spec-card format)
   * `'labeled'` → `35.9" W × 72.9" H × 28.8" D` (recommended for clarity)
   */
  style?: 'compact' | 'labeled'
  /**
   * If true, renders even when status is `invalid` or `mixed` (with a
   * fallback string). Default false — we'd rather omit than mislead.
   */
  renderInvalid?: boolean
  /** Custom string for the invalid case if `renderInvalid=true`. */
  invalidPlaceholder?: string
}

/**
 * Format a {@link NormalizedDimensions} object for display.
 *
 * Returns empty string when status is `invalid`, `missing`, or `mixed`
 * (unless `renderInvalid` is set). This is intentional: a renderer that
 * receives "" should hide the dimensions row entirely rather than emit
 * something like `null" × null" × null"`.
 */
export function formatDimensions(
  normalized: NormalizedDimensions | null | undefined,
  options: FormatDimensionsOptions = {},
): string {
  if (!normalized) return ''
  const { style = 'labeled', renderInvalid = false, invalidPlaceholder = '' } = options

  if (normalized.status === 'invalid' || normalized.status === 'missing' || normalized.status === 'mixed') {
    return renderInvalid ? invalidPlaceholder : ''
  }

  const { widthInches, heightInches, depthInches } = normalized

  if (style === 'compact') {
    const parts = [widthInches, heightInches, depthInches]
      .map(formatDimensionInches)
      .filter(Boolean)
    return parts.join(' × ')
  }

  // labeled
  const parts: string[] = []
  if (widthInches) parts.push(`${formatDimensionInches(widthInches)} W`)
  if (heightInches) parts.push(`${formatDimensionInches(heightInches)} H`)
  if (depthInches) parts.push(`${formatDimensionInches(depthInches)} D`)
  return parts.join(' × ')
}

/**
 * Convenience: take raw DB fields and return a normalized + formatted string
 * in one call. Renderers that only need the final string can use this.
 *
 * @example
 *   formatRawDimensions({ width_in: 912, height_in: 1853, depth_in: 731 }, 'counter-depth')
 *   // → '35.9" W × 73" H × 28.8" D'
 */
export function formatRawDimensions(
  raw: RawDimensions,
  productType?: string | null,
  options: FormatDimensionsOptions = {},
): string {
  return formatDimensions(normalizeDimensions(raw, productType), options)
}

/**
 * Standalone sanity check that returns a warning string when something looks
 * wrong, or null when fine. Used by import scripts and admin tooling — the
 * normalizer above is the runtime safety net.
 */
export function sanityCheckDimensions(
  raw: RawDimensions,
  productType?: string | null,
): string | null {
  const result = normalizeDimensions(raw, productType)
  if (result.status === 'invalid') return result.reason || 'invalid dimensions'
  if (result.status === 'inferred_mm') {
    return `dimensions stored as mm (W=${raw.width_in} H=${raw.height_in} D=${raw.depth_in}) but field name is *_in — should be normalized at ingestion`
  }
  if (result.status === 'inferred_cm') {
    return `dimensions stored as cm (W=${raw.width_in} H=${raw.height_in} D=${raw.depth_in}) but field name is *_in — should be normalized at ingestion`
  }
  return null
}

/**
 * A simple boolean guard exposing whether a raw record has dimensions safe
 * to render verbatim. Useful for prose generators that should fall back to
 * a generic claim when dims are missing/uncertain.
 */
export function hasReliableDimensions(raw: RawDimensions, productType?: string | null): boolean {
  const result = normalizeDimensions(raw, productType)
  return (
    (result.status === 'explicit_inches' || result.status === 'inferred_mm' || result.status === 'inferred_cm')
    && (result.widthInches !== null || result.heightInches !== null || result.depthInches !== null)
  )
}
